/**
 * Przewidywany koszt konstrukcji montażowej pod moduły PV.
 *
 * ŹRÓDŁO: zestawienie ofertowe VoltValue — średnie koszty konstrukcji w zł/kWp
 * dla różnych rodzajów połaci, policzone na kilku kombinacjach układu modułów
 * (np. 3 × 8, 2 × 12, 4 × 6) przy module 415 W. Wartości w `KONSTRUKCJE` to
 * dokładnie te średnie, przepisane z zestawienia bez zaokrąglania w drugą stronę.
 *
 * ═══ TRZY RZECZY, KTÓRE TEN MODUŁ MODELUJE ═══
 *
 * 1. RODZAJ POŁACI. Różnica jest ogromna i to ona rządzi wynikiem: blacha
 *    trapezowa 229 zł/kWp wobec dachówki ceramicznej 356 zł/kWp — ponad połowa
 *    więcej za tę samą instalację. Bierze się z tego, jak mocuje się konstrukcję:
 *    do blachy trapezowej wkręca się wprost, pod dachówkę trzeba haki i ingerencję
 *    w pokrycie.
 *
 * 2. WIELKOŚĆ INSTALACJI. Zestawienie ma jeden rodzaj połaci (blachodachówka)
 *    policzony dla 5, 10 i 20 kWp: 277,07 → 249,39 → 242,94 zł/kWp. Koszt
 *    jednostkowy maleje, bo część roboty i osprzętu nie zależy od liczby modułów.
 *    Spadek WYRAŹNIE się wypłaszcza (−11% z 5 na 10 kWp, ale już tylko −2,6%
 *    z 10 na 20), więc interpolujemy odcinkami po logarytmie mocy, a nie jedną
 *    prostą — prosta zaniżyłaby duże instalacje.
 *
 * 3. MOC MODUŁU. Konstrukcja kosztuje od SZTUKI modułu (szyny, klemy, haki),
 *    a nie od kilowata, więc koszt na kWp jest odwrotnie proporcjonalny do mocy
 *    modułu. Zestawienie to potwierdza: kolumna dla 460 W jest średnio o ok. 12%
 *    tańsza od 415 W, a sam stosunek mocy daje 415/460 = 0,90, czyli 10%.
 *    Zgodność modelu fizycznego z danymi w granicach szumu — jedna kombinacja
 *    na rodzaj połaci w kolumnie 460 W to za mało, żeby dopasowywać cokolwiek
 *    bardziej wymyślnego.
 *
 * CZEGO TU NIE MA: montażu w poziomie. Wartości `poziom` są przepisane, bo są
 * w zestawieniu i przydadzą się, gdy dołożymy ten wybór — ale dziś liczymy
 * WYŁĄCZNIE pion (patrz `ORIENTACJA_DOMYSLNA`).
 */

/** Moc modułu, dla której policzono wartości bazowe. */
import { zZlotych } from '../kraj'

export const MOC_ODNIESIENIA_W = 415

/** Wielkość instalacji, dla której podano wartości bazowe. */
export const MOC_BAZOWA_KWP = 10

/**
 * Na razie liczymy tylko montaż pionowy — tak ustalono przy wprowadzaniu modułu.
 * Wartości dla poziomu są w tabeli i czekają na osobny wybór w interfejsie.
 */
export const ORIENTACJA_DOMYSLNA = 'pion'

/**
 * Rodzaje połaci. `pion`/`poziom` to zł/kWp przy module 415 W i instalacji 10 kWp.
 *
 * `pion: null` przy dachu balastowym nie jest brakiem danych: na płaskim dachu
 * konstrukcję balastową stawia się w układzie poziomym i pionowy wariant nie
 * istnieje. Liczymy wtedy z tego, co jest — patrz `stawkaBazowa`.
 */
/** Obrazek rodzaju konstrukcji (`public/img/konstrukcje/<key>.webp`, 192 px, od właściciela 2026-10-11); null dla nieznanego. */
export const obrazekKonstrukcji = (key) => (KONSTRUKCJE.some((k) => k.key === key) ? `${process.env.PUBLIC_URL || ''}/img/konstrukcje/${key}.webp` : null)

export const KONSTRUKCJE = [
  { key: 'blacha_trapezowa', label: 'Blacha trapezowa', pion: 229.17, poziom: 227.80, domyslny: true },
  { key: 'blachodachowka', label: 'Blachodachówka', pion: 249.39, poziom: 347.41 },
  { key: 'membrana_papa', label: 'Membrana lub papa (dach skośny)', pion: 284.69, poziom: 443.97 },
  { key: 'dachowka_bitumiczna', label: 'Dachówka bitumiczna', pion: 286.08, poziom: 402.46 },
  { key: 'rabek', label: 'Blacha na rąbek', pion: 324.17, poziom: 501.37 },
  { key: 'dachowka_karpiowka', label: 'Dachówka karpiówka', pion: 332.27, poziom: 477.57 },
  { key: 'dachowka_ceramiczna', label: 'Dachówka ceramiczna', pion: 355.88, poziom: 517.75 },
  { key: 'balastowy_plaski', label: 'Dach płaski, konstrukcja balastowa', pion: null, poziom: 342.47 },
  { key: 'grunt', label: 'Grunt, konstrukcja wbijana', pion: 544.22, poziom: 528.23 },
]

export const KONSTRUKCJA_DOMYSLNA = KONSTRUKCJE.find((k) => k.domyslny).key

export const konstrukcjaWg = (key) =>
  KONSTRUKCJE.find((k) => k.key === key) || KONSTRUKCJE.find((k) => k.domyslny)

/**
 * Punkty pomiarowe zależności ceny od wielkości instalacji — z jedynego rodzaju
 * połaci policzonego w zestawieniu dla trzech mocy (blachodachówka). Zakładamy,
 * że kształt tej krzywej jest wspólny dla wszystkich pokryć: rozkłada się na nią
 * ta sama robocizna i ten sam osprzęt niezależny od liczby modułów.
 */
const SKALA_KWP = [
  { kwp: 5, wsp: 277.07 / 249.39 },
  { kwp: 10, wsp: 1 },
  { kwp: 20, wsp: 242.94 / 249.39 },
]

/**
 * Współczynnik wielkości instalacji. Interpolacja ODCINKAMI po logarytmie mocy:
 * spadek ceny jednostkowej jest szybki na początku i prawie płaski powyżej 10 kWp,
 * więc jedna prosta przez trzy punkty zaniżałaby instalacje duże i zawyżała małe.
 *
 * Poza zakresem 5–20 kWp trzymamy wartość skrajną, zamiast przedłużać trend:
 * ekstrapolacja poza dane to zgadywanie, a przy 50 kWp dałaby liczbę, której
 * nic nie potwierdza.
 */
export const wspolczynnikSkali = (kwp) => {
  const m = Number(kwp)
  if (!Number.isFinite(m) || m <= 0) return 1
  if (m <= SKALA_KWP[0].kwp) return SKALA_KWP[0].wsp
  if (m >= SKALA_KWP[SKALA_KWP.length - 1].kwp) return SKALA_KWP[SKALA_KWP.length - 1].wsp

  for (let i = 1; i < SKALA_KWP.length; i += 1) {
    const a = SKALA_KWP[i - 1]
    const b = SKALA_KWP[i]
    if (m <= b.kwp) {
      const t = (Math.log(m) - Math.log(a.kwp)) / (Math.log(b.kwp) - Math.log(a.kwp))
      return a.wsp + (b.wsp - a.wsp) * t
    }
  }
  return 1
}

/**
 * Współczynnik mocy modułu. Konstrukcja kosztuje od sztuki, więc mocniejszy moduł
 * to mniej sztuk na kilowat i taniej za kWp.
 *
 * Ograniczone do 0,75–1,35, bo poza typowym zakresem mocy modułów (ok. 300–550 W)
 * ta prosta proporcja przestaje mieć pokrycie w czymkolwiek zmierzonym.
 */
export const wspolczynnikMocyModulu = (mocW) => {
  const m = Number(mocW)
  if (!Number.isFinite(m) || m <= 0) return 1
  return Math.min(1.35, Math.max(0.75, MOC_ODNIESIENIA_W / m))
}

/** Stawka bazowa dla rodzaju połaci; przy braku pionu (dach balastowy) bierzemy poziom.
 *  W walucie kraju: polskie stawki przeliczone kursem (patrz config/cennikMontazu). */
export const stawkaBazowa = (konstrukcja, orientacja = ORIENTACJA_DOMYSLNA) => {
  const k = konstrukcjaWg(konstrukcja)
  return zZlotych(k[orientacja] ?? k.poziom ?? k.pion)
}

/**
 * Przewidywany koszt konstrukcji.
 *
 * @param {Object} p
 * @param {number} p.kwp        moc instalacji
 * @param {string} p.konstrukcja klucz rodzaju połaci
 * @param {number} [p.mocModuluW] moc pojedynczego modułu; brak = wartość odniesienia
 */
export const kosztKonstrukcji = ({ kwp, konstrukcja, mocModuluW, orientacja = ORIENTACJA_DOMYSLNA }) => {
  const moc = Number(kwp)
  const k = konstrukcjaWg(konstrukcja)
  const bazowa = stawkaBazowa(konstrukcja, orientacja)
  if (!Number.isFinite(moc) || moc <= 0 || bazowa == null) return null

  const skala = wspolczynnikSkali(moc)
  const wspMocy = wspolczynnikMocyModulu(mocModuluW || MOC_ODNIESIENIA_W)
  const zlKwp = bazowa * skala * wspMocy

  return {
    konstrukcja: k.key,
    etykieta: k.label,
    orientacja: k[orientacja] != null ? orientacja : 'poziom',
    zlZaKwp: Math.round(zlKwp * 100) / 100,
    razemZl: Math.round(zlKwp * moc),
    stawkaBazowaZlKwp: bazowa,
    wspolczynnikSkali: Math.round(skala * 1000) / 1000,
    wspolczynnikMocy: Math.round(wspMocy * 1000) / 1000,
  }
}
